import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, Plus, Search, Pencil, Trash2, Power, X, Check, Clock3, IndianRupee, RotateCcw, ChevronLeft, ChevronRight, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { getMembershipPlans, createMembershipPlan, updateMembershipPlan, updateMembershipPlanStatus, deleteMembershipPlan } from "../../api/membershipPlanService";
const emptyPlan = { plan_name_en: "", plan_name_ta: "", duration_days: "365", amount: "", gst_percentage: "18", gst_type: "exclusive", discount_amount: "0", benefits_en: "", benefits_ta: "" };

function formatPrice(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function getStatus(status) {
  return String(status).toLowerCase() === "active"
    ? "Active"
    : "Inactive";
}

function getPlanCode(rfId) {
  return `PLAN${String(rfId).padStart(3, "0")}`;
}

function StatusBadge({ status }) {
  const active = status === "Active";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold
      ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-slate-400"}`} />
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color, description }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500">{title}</p>
          <h3 className="mt-2 text-2xl font-bold text-slate-800">{value}</h3>
          <p className="mt-2 text-[11px] text-slate-400">{description}</p>
        </div>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${colors[color]}`}>
          <Icon size={21} />
        </div>
      </div>
    </div>
  );
}

function PlanModal({ plan, onClose, onSave, saving, }) {
  const [form, setForm] = useState(() =>
    plan ? {
      plan_name_en: plan.plan_name_en || "",
      plan_name_ta: plan.plan_name_ta || "",
      duration_days: String(plan.duration_days || 365),
      amount: String(plan.amount ?? ""),
      gst_percentage: String(plan.gst_percentage ?? 18),
      gst_type: plan.gst_type || "exclusive",
      discount_amount: String(plan.discount_amount ?? 0),
      benefits_en: plan.benefits_en || "",
      benefits_ta: plan.benefits_ta || "",
    }
      : { ...emptyPlan }
  );
  const [error, setError] = useState("");
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value, }));
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.plan_name_en.trim()) {
      setError("Please enter the English plan name.");
      return;
    }
    if (!form.plan_name_ta.trim()) {
      setError("Please enter the Tamil plan name.");
      return;
    }
    if (
      !form.duration_days ||
      Number(form.duration_days) <= 0
    ) {
      setError("Please enter a valid duration.");
      return;
    }
    if (form.amount === "" || Number(form.amount) < 0) {
      setError("Please enter a valid plan amount.");
      return;
    }
    if (
      form.gst_percentage === "" ||
      Number(form.gst_percentage) < 0 ||
      Number(form.gst_percentage) > 100
    ) {
      setError("GST percentage must be between 0 and 100.");
      return;
    }
    if (
      form.discount_amount === "" ||
      Number(form.discount_amount) < 0 ||
      Number(form.discount_amount) > Number(form.amount)
    ) {
      setError("Discount cannot exceed the plan amount.");
      return;
    }
    const payload = {
      plan_name_en: form.plan_name_en.trim(),
      plan_name_ta: form.plan_name_ta.trim(),
      duration_days: Number(form.duration_days),
      amount: Number(form.amount),
      gst_percentage: Number(form.gst_percentage),
      gst_type: form.gst_type,
      discount_amount: Number(form.discount_amount),
      benefits_en: form.benefits_en.trim(),
      benefits_ta: form.benefits_ta.trim(),
    };
    try {
      await onSave(payload);
    } catch (err) {
      setError(err.message || "Unable to save membership plan.");
    }
  };

  const inputClass = "mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";
  const labelClass = "text-xs font-semibold text-slate-600";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/50 p-3 sm:p-5">
      <div className="my-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{plan ? "Edit Membership Plan" : "Add Membership Plan"}</h2>
            <p className="mt-1 text-xs text-slate-500">Configure membership duration, pricing, GST and benefits.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close modal"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
            <X size={19} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="max-h-[70vh] space-y-5 overflow-y-auto p-5 sm:p-6">
            <div>
              <h3 className="mb-3 text-sm font-bold text-slate-800">Plan Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Plan Name (English) *</label>
                  <input
                    required
                    value={form.plan_name_en}
                    onChange={(e) => updateField("plan_name_en", e.target.value)}
                    placeholder="e.g. Gold Plan"
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Plan Name (Tamil) *</label>
                  <input
                    required
                    value={form.plan_name_ta}
                    onChange={(e) => updateField("plan_name_ta", e.target.value)}
                    placeholder="தங்கத் திட்டம்"
                    className={inputClass} />
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-bold text-slate-800">Plan Pricing</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Duration (Days) *</label>
                  <input
                    required
                    type="number"
                    min="1"
                    value={form.duration_days}
                    onChange={(e) => updateField("duration_days", e.target.value)}
                    placeholder="365"
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Plan Amount (₹) *</label>
                  <input
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => updateField("amount", e.target.value)}
                    placeholder="Enter amount"
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>GST Percentage (%) *</label>
                  <input
                    required
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.gst_percentage}
                    onChange={(e) => updateField("gst_percentage", e.target.value)}
                    placeholder="18"
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>GST Type *</label>
                  <select
                    value={form.gst_type}
                    onChange={(e) => updateField("gst_type", e.target.value)}
                    className={inputClass}>
                    <option value="exclusive">GST Exclusive</option>
                    <option value="inclusive">GST Inclusive</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Discount Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discount_amount}
                    onChange={(e) => updateField("discount_amount", e.target.value)}
                    placeholder="0"
                    className={inputClass} />
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
              <h3 className="text-xs font-bold text-slate-700">Pricing Summary</h3>
              {(() => {
                const amount = Number(form.amount) || 0;
                const discount = Number(form.discount_amount) || 0;
                const gst = Number(form.gst_percentage) || 0;
                const discounted = amount - discount;
                const gstAmount = form.gst_type === "inclusive" ? discounted - discounted / (1 + gst / 100) : discounted * (gst / 100);
                const finalAmount = form.gst_type === "inclusive" ? discounted : discounted + gstAmount;
                return (
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                    <div>
                      <p className="text-slate-500">Amount</p>
                      <p className="mt-1 font-bold text-slate-800">{formatPrice(amount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Discount</p>
                      <p className="mt-1 font-bold text-rose-600">{formatPrice(discount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">GST Amount</p>
                      <p className="mt-1 font-bold text-slate-800">{formatPrice(gstAmount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Final Amount</p>
                      <p className="mt-1 font-bold text-blue-700">{formatPrice(finalAmount)}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div>
              <label className={labelClass}>Plan Benefits (English)</label>
              <textarea
                rows={4}
                value={form.benefits_en}
                onChange={(e) => updateField("benefits_en", e.target.value)}
                placeholder={"Enter one benefit per line\nView member profiles\nContact details access"}
                className={inputClass} />
              <p className="mt-1.5 text-[10px] text-slate-400">Enter each benefit on a separate line.</p>
            </div>
            <div>
              <label className={labelClass}>Plan Benefits (Tamil)</label>
              <textarea
                rows={4}
                value={form.benefits_ta}
                onChange={(e) => updateField("benefits_ta", e.target.value)}
                placeholder="ஒவ்வொரு நன்மையையும் தனித்தனி வரியில் உள்ளிடவும்"
                className={inputClass} />
              <p className="mt-1.5 text-[10px] text-slate-400">Enter each benefit on a separate line.</p>
            </div>
            {error && (
              <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {saving ? "Saving..." : plan ? "Save Changes" : "Create Plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteModal({ plan, onClose, onConfirm, deleting }) {
  if (!plan) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
          <Trash2 size={22} />
        </div>
        <h3 className="mt-4 text-center text-lg font-bold text-slate-800">Delete Membership Plan?</h3>
        <p className="mt-2 text-center text-sm text-slate-500">Are you sure you want to delete{" "}<strong>{plan.plan_name_en}</strong>?</p>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} disabled={deleting} className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertModal({ alert, onClose, onRetry }) {
  if (!alert) return null;
  const isSuccess = alert.type === "success";
  const Icon = isSuccess ? CheckCircle2 : AlertCircle;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="membership-alert-title"
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className={`flex justify-center pt-7 ${isSuccess ? "text-emerald-600" : "text-rose-600"}`}>
          <div className={`flex h-16 w-16 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-50" : "bg-rose-50"}`}>
            <Icon size={34} strokeWidth={1.8} />
          </div>
        </div>
        <div className="px-6 pb-6 pt-4 text-center">
          <h3 id="membership-alert-title" className="text-lg font-bold text-slate-800">{alert.title || (isSuccess ? "Success" : "Something went wrong")}</h3>
          <p className="mt-2 break-words text-sm leading-6 text-slate-500">{alert.message}</p>
          <div className="mt-6 flex justify-center gap-2">
            {alert.retry && (
              <button type="button" onClick={onRetry} className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                Retry
              </button>
            )}
            <button type="button" onClick={onClose} className={`rounded-lg px-6 py-2.5 text-sm font-semibold text-white ${isSuccess ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MembershipPlans() {
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMembershipPlans();
      const apiPlans = response.data || [];
      const formattedPlans = apiPlans.map((plan) => ({
        ...plan,
        id: plan.rfId,
        code: getPlanCode(plan.rfId),
        price: Number(plan.amount) || 0,
        status: getStatus(plan.status),
        features: plan.benefits_en ? plan.benefits_en.split("\n").map((item) => item.trim()).filter(Boolean) : [],
      }));
      setPlans(formattedPlans);
    } catch (err) {
      setAlert({ type: "error", title: "Unable to Load Plans", message: err.message || "Unable to fetch membership plans.", retry: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const stats = useMemo(() => [{
    title: "Total Plans",
    value: plans.length,
    icon: CreditCard,
    color: "blue",
    description: "All membership plans",
  },
  {
    title: "Active Plans",
    value: plans.filter((p) => p.status === "Active").length,
    icon: Check,
    color: "green",
    description: "Available for purchase",
  },
  {
    title: "Inactive Plans",
    value: plans.filter((p) => p.status === "Inactive").length,
    icon: Power,
    color: "amber",
    description: "Unavailable for purchase",
  },
  {
    title: "Highest Plan Price",
    value: formatPrice(Math.max(0, ...plans.map((p) => Number(p.pricing?.final_amount ?? p.price)))),
    icon: IndianRupee,
    color: "violet",
    description: "Maximum configured price",
  },
  ],
    [plans]
  );

  const filteredPlans = useMemo(() => {
    const term = search.trim().toLowerCase();
    return plans.filter((plan) => {
      const matchesSearch =
        !term ||
        plan.plan_name_en?.toLowerCase().includes(term) ||
        plan.plan_name_ta?.toLowerCase().includes(term) ||
        plan.code?.toLowerCase().includes(term);
      return (
        matchesSearch &&
        (!statusFilter || plan.status === statusFilter)
      );
    });
  }, [plans, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const visiblePlans = filteredPlans.slice(startIndex, startIndex + pageSize);
  const resetFilters = () => { setSearch(""); setStatusFilter(""); setPage(1); };
  const savePlan = async (form) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      if (editingPlan) {
        await updateMembershipPlan(editingPlan.rfId, form);
        setAlert({ type: "success", title: "Plan Updated", message: "Membership plan updated successfully." });
      } else {
        await createMembershipPlan(form);
        setAlert({ type: "success", title: "Plan Created", message: "Membership plan created successfully." });
        setPage(1);
      }
      setModalOpen(false);
      setEditingPlan(null);
      await fetchPlans();
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const toggleStatus = async (plan) => {
    if (statusLoadingId) return;
    const nextStatus = plan.status === "Active" ? "inactive" : "active";
    setStatusLoadingId(plan.rfId);
    try {
      await updateMembershipPlanStatus(plan.rfId, nextStatus);
      setAlert({ type: "success", title: "Status Updated", message: `${plan.plan_name_en} is now ${nextStatus}.` });
      await fetchPlans();
    } catch (err) {
      setAlert({ type: "error", title: "Status Update Failed", message: err.message || "Unable to update plan status." });
    } finally {
      setStatusLoadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletePlan || deleting) return;
    setDeleting(true);
    try {
      await deleteMembershipPlan(deletePlan.rfId);
      setAlert({ type: "success", title: "Plan Deleted", message: "Membership plan deleted successfully." });
      setDeletePlan(null);
      await fetchPlans();
    } catch (err) {
      setAlert({ type: "error", title: "Delete Failed", message: err.message || "Unable to delete membership plan." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-400">
            <span>Home</span>
            <span>/</span>
            <span>Membership Plans</span>
            <span>/</span>
            <span className="font-medium text-blue-600">Plan List</span>
          </div>
          <h1 className="text-xl font-bold text-slate-800 sm:text-2xl">Membership Plans</h1>
          <p className="mt-1 text-xs text-slate-500 sm:text-sm">Create and manage membership packages and pricing.</p>
        </div>
        <button
          type="button"
          onClick={() => { setEditingPlan(null); setModalOpen(true); }}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-xs font-semibold text-white shadow-sm hover:bg-blue-700">
          <Plus size={17} />
          Add Membership Plan
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-slate-800">Search & Filters</h2>
          <p className="mt-1 text-xs text-slate-400">Search plans by name or plan code.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search plan name or code"
              className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-xs outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 outline-none focus:border-blue-400">
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">Found{" "}<strong className="text-slate-800">{filteredPlans.length}</strong>{" "}plan(s)</p>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <RotateCcw size={14} />
            Reset Filters
          </button>
        </div>
      </section>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Membership Plan List</h2>
            <p className="mt-1 text-[11px] text-slate-400">Manage plan details, availability and pricing.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            Show
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
            entries
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left">
            <thead className="bg-slate-50">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Plan</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3">Benefits</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-14 text-center">
                    <Loader2 size={28} className="mx-auto animate-spin text-blue-600" />
                    <p className="mt-3 text-sm font-medium text-slate-600">Loading membership plans...</p>
                  </td>
                </tr>
              ) : (
                <>
                  {visiblePlans.map((plan) => (
                    <tr key={plan.rfId} className="transition hover:bg-slate-50/70">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                            <CreditCard size={19} />
                          </div>
                          <div>
                            <p className="whitespace-nowrap text-xs font-semibold text-slate-800">{plan.plan_name_en}</p>
                            <p className="mt-1 text-xs text-slate-500">{plan.plan_name_ta}</p>
                            <p className="mt-1 text-[10px] text-slate-400">{plan.code}</p>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <div className="flex items-center gap-2 text-xs text-slate-600">
                          <Clock3 size={14} className="text-slate-400" />
                          {plan.duration_days} Days
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <p className="text-sm font-bold text-slate-800">{formatPrice(plan.pricing?.final_amount ?? plan.price)}</p>
                        {Number(plan.discount_amount) > 0 && (
                          <p className="mt-1 text-[10px] text-rose-600">Discount: {formatPrice(plan.discount_amount)}</p>
                        )}
                        <p className="mt-1 text-[10px] text-slate-400">{plan.gst_type === "inclusive" ? "GST Inclusive" : "GST Exclusive"}{" · "}{plan.gst_percentage}% GST</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1.5">
                          {(plan.features || []).slice(0, 3).map((feature, index) => (
                            <div key={`${plan.rfId}-${index}`} className="flex items-start gap-1.5 text-[10px] text-slate-600">
                              <Check size={12} className="mt-0.5 shrink-0 text-emerald-500" />
                              <span>{feature}</span>
                            </div>
                          ))}
                          {(plan.features || []).length > 3 && (
                            <p className="pl-4 text-[10px] font-medium text-blue-600">+{plan.features.length - 3} more benefits</p>
                          )}
                          {!(plan.features || []).length && (
                            <span className="text-[10px] text-slate-400">No benefits added</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <StatusBadge status={plan.status} />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title={plan.status === "Active" ? "Deactivate plan" : "Activate plan"}
                            aria-label={`Change status for ${plan.plan_name_en}`}
                            disabled={statusLoadingId === plan.rfId}
                            onClick={() => toggleStatus(plan)}
                            className={`rounded-lg p-2 disabled:opacity-50 ${plan.status === "Active"
                              ? "text-rose-600 hover:bg-rose-50"
                              : "text-emerald-600 hover:bg-emerald-50"
                              }`}>
                            {statusLoadingId === plan.rfId ? (
                              <Loader2 size={15} className="animate-spin" />
                            ) : (
                              <Power size={15} />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Edit plan"
                            aria-label={`Edit ${plan.plan_name_en}`}
                            onClick={() => { setEditingPlan(plan); setModalOpen(true); }}
                            className="rounded-lg p-2 text-amber-600 hover:bg-amber-50">
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            title="Delete plan"
                            aria-label={`Delete ${plan.plan_name_en}`}
                            onClick={() => setDeletePlan(plan)}
                            className="rounded-lg p-2 text-rose-600 hover:bg-rose-50">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visiblePlans.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-14 text-center">
                        <CreditCard size={28} className="mx-auto text-slate-300" />
                        <p className="mt-3 text-sm font-semibold text-slate-700">No membership plans found</p>
                        <p className="mt-1 text-xs text-slate-400">Try another search or change the status filter.</p>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-[11px] text-slate-500">
            Showing {filteredPlans.length ? startIndex + 1 : 0} to{" "}
            {Math.min(startIndex + pageSize, filteredPlans.length)} of{" "}
            {filteredPlans.length} entries
          </p>
          <div className="flex items-center gap-1 self-end sm:self-auto">
            <button
              type="button"
              disabled={safePage <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Previous page">
              <ChevronLeft size={15} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).filter(
              (number) =>
                number === 1 ||
                number === totalPages ||
                Math.abs(number - safePage) <= 1
            ).map((number, index, numbers) => (
              <React.Fragment key={number}>
                {index > 0 &&
                  number - numbers[index - 1] > 1 && (
                    <span className="px-1 text-xs text-slate-400">...</span>
                  )}
                <button
                  type="button"
                  onClick={() => setPage(number)}
                  className={`h-8 min-w-8 rounded-lg px-2 text-xs font-medium ${safePage === number ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {number}
                </button>
              </React.Fragment>
            ))}
            <button
              type="button"
              disabled={safePage >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-slate-200 p-2 text-slate-500 disabled:opacity-40"
              aria-label="Next page">
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </section>
      {modalOpen && (
        <PlanModal
          plan={editingPlan}
          onClose={() => { if (actionLoading) return; setModalOpen(false); setEditingPlan(null); }}
          onSave={savePlan}
          saving={actionLoading} />
      )}

      <DeleteModal
        plan={deletePlan}
        onClose={() => { if (!deleting) setDeletePlan(null); }}
        onConfirm={confirmDelete}
        deleting={deleting} />

      <AlertModal
        alert={alert}
        onClose={() => setAlert(null)}
        onRetry={() => { setAlert(null); fetchPlans(); }} />
    
    </div>
  );
}